/**
 * WorkItemCard — the pipeline card (mockup v2, locked layout):
 *   Row 1  client name (serif) · primary date
 *   Row 2  unit (color dot + name) · service
 *   Row 3  stage/progress chip (the urgency signal) · owner-led avatar stack
 *
 * Polish batch: the primary date is the DUE / next-action date when set (the team
 * scans for it; terracotta when overdue), else the arrival date (fix #4); the owner
 * avatar is a button that assigns/reassigns the process owner when `onAssign` is
 * given (fix #2); roomier padding (fix #5). The card is a role=button div (not a
 * <button>) so the avatar button can nest without invalid markup.
 *
 * Name + unit are read-resolved (display_name = clients.display_name once converted
 * at Client Setup, else prospect_name). Presentational — DnD lives in PipelineBoard.
 */

import { type KeyboardEvent, type ReactNode } from 'react';
import { Paperclip } from 'lucide-react';
import type { WorkItemRow } from '@/lib/api';
import { displayValue } from '@/lib/valueCase';
import type { ProcessCardFolderLink } from '@/lib/processCardFields';
import { ASSIGNED_STAGE_KEY, READY_FOR_ASSIGNMENT_STAGE_KEY } from '@/lib/workspaceStageMove';
import { avatarClasses, cardName, categoryDotColor, collaboratorStack, initials, isOverdue, nextActionPassed, shortDate, unitDotColor } from './workspaceFormat';

export interface WorkItemCardProps {
  item: WorkItemRow;
  onOpen: (id: string) => void;
  /** Polish fix #2: when provided, the owner avatar becomes an assign/reassign button. */
  onAssign?: (item: WorkItemRow) => void;
  /** Field pack v1: the board's configured due-date label (e.g. a hiring
   *  board's "Application date"). Process cards drop the "due" reading and
   *  show the date under this label instead; intake cards ignore it. */
  dueDateLabel?: string | null;
  /** Founder soak: the column's stage key, when the card renders inside a
   *  pipeline column. Drives the Case Assignment extras (prospect-folder link
   *  + the always-visible Care manager row) on intake cards; omit elsewhere
   *  (agenda lists, drag overlay) and the card renders exactly as before. */
  stageKey?: string | null;
  /** The board's configured folder link (a process board's talent folder,
   *  say). A process card renders it under its label when the card has no
   *  per-card allows_link URL; intake cards ignore it (their link is the
   *  stage-gated prospect folder). */
  folderLink?: ProcessCardFolderLink | null;
}

export function WorkItemCard({ item, onOpen, onAssign, dueDateLabel = null, stageKey = null, folderLink = null }: WorkItemCardProps): ReactNode {
  // Field pack v1: a relabeled process-card date (e.g. "Application date")
  // is a record, not a deadline — the whole overdue register (chip bit +
  // pepper tint) is off for it.
  const overdue = isOverdue(item) && !(item.kind === 'process_item' && dueDateLabel);
  const assigned = item.assignee_name !== null;
  const name = item.display_name ?? cardName(item);
  const arrival = item.contact_date ?? item.created_at; // arrival (no due set)
  const due = item.due_date; // fix #4: the next-action date, primary when set
  const total = item.checklist_total;
  const done = item.checklist_done;
  const complete = total > 0 && done >= total;
  const dot = unitDotColor(item.unit_id, item.unit_color);
  // Shared assignment (0108): owner-led avatar stack — owner + up to 2 collaborators, then "+N".
  const { visible: shownCollaborators, overflow: collaboratorOverflow } = collaboratorStack(item.collaborators ?? [], 2);

  // Founder soak — the Case Assignment extras (intake cards only; a process
  // board reusing these bare stage keys has no CM/folder semantics):
  //  · the Care manager row renders on EVERY card in the assignment cluster
  //    (Ready for Assignment + Assigned) so an unassigned CM is visibly
  //    "Unassigned" instead of a ghost state (a units-only assign used to
  //    leave a CM-less card with no signal at all);
  //  · the prospect-folder link renders in Ready for Assignment when the
  //    Template v2 (0124) checklist captured one — one-click access for unit
  //    leads doing assignment. No link entered, no affordance.
  const isIntake = item.kind === 'intake';
  const cmAssigned = item.assignee_roster_id !== null;
  // When a roster CM is set, assignee_name IS the roster name (the list read
  // resolves roster first); when unset it may be the desk owner, so never
  // show it on this row.
  const cmName = cmAssigned ? item.assignee_name : null;
  const showCmRow = isIntake && (stageKey === READY_FOR_ASSIGNMENT_STAGE_KEY || stageKey === ASSIGNED_STAGE_KEY);
  // A process card (talent, say) links to its folder from every lane: the
  // per-card checklist URL when one was captured, else the board's
  // configured folder. Same affordance as the intake card, whose link stays
  // gated to Ready for Assignment.
  const isProcessCard = item.kind === 'process_item';
  const processFolderUrl = isProcessCard ? (item.folder_link_url ?? folderLink?.url ?? null) : null;
  const folderUrl = isIntake && stageKey === READY_FOR_ASSIGNMENT_STAGE_KEY ? item.folder_link_url : processFolderUrl;
  const folderLabel = isProcessCard ? (folderLink?.label ?? 'Folder') : 'Prospect folder';

  // Card attachments (0162): the face shows only THAT files exist, and
  // how many. The list read resolves it as a grouped sub-count.
  const attachmentCount = item.attachment_count ?? 0;
  // The day to touch this card next. Its own line, never the primary date:
  // the due date keeps row 1 and its overdue register. Terracotta once the
  // day has passed on an open card (a nudge, not a deadline miss).
  const nextAction = item.next_action_date;
  const nextActionLate = nextActionPassed(item);
  // A process card's values: the full set when the board is multi-valued,
  // else the one category (a legacy row carries category with no set).
  const categoryValues: string[] = item.categories ?? (item.category ? [item.category] : []);

  const chipBits: string[] = [];
  if (item.current_stage_label) chipBits.push(item.current_stage_label);
  if (total > 0) chipBits.push(`${done}/${total}`);
  if (overdue) chipBits.push('overdue');
  const chipClass = overdue
    ? 'bg-pepper-soft text-pepper'
    : complete
      ? 'bg-sage-50 text-sage-400'
      : 'bg-gold-soft text-warm-700';

  // role=button div: only act on keys targeted AT the card, not bubbled from the
  // nested avatar button (else Enter on the avatar would also open the drawer).
  function onKey(e: KeyboardEvent): void {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(item.id);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="work-item-card"
      data-work-item-id={item.id}
      onClick={() => onOpen(item.id)}
      onKeyDown={onKey}
      className="w-full text-left bg-paper rounded-card shadow-sh1 hover:shadow-sh2 overflow-hidden transition-shadow duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      {/* fix #5: roomier padding (px-4 py-3.5) + a touch more row spacing */}
      <div className="px-4 py-3.5">
        {/* Row 1 — name + primary date (due when set, else arrival) */}
        <div className="flex items-baseline gap-2">
          <p className="font-display text-[15px] font-semibold text-sage-800 leading-tight min-w-0 flex-1 truncate">{name}</p>
          {due ? (
            // Field pack v1: a process card with a configured date label (e.g.
            // "Application date") reads as that date, not a deadline — no "due"
            // prefix, no overdue tint (an application date can't be overdue).
            item.kind === 'process_item' && dueDateLabel ? (
              <span data-testid="card-date" title={dueDateLabel} className="text-[11px] font-semibold flex-shrink-0 text-warm-700">
                {shortDate(due)}
              </span>
            ) : (
            <span data-testid="card-date" title="Due / next action" className={'text-[11px] font-semibold flex-shrink-0 ' + (overdue ? 'text-pepper' : 'text-warm-700')}>
              due {shortDate(due)}
            </span>
            )
          ) : arrival ? (
            <span data-testid="card-date" className="text-[11px] text-sage-400 flex-shrink-0">{shortDate(arrival)}</span>
          ) : null}
        </div>

        {/* Row 2 — unit (color dot + name) · service. Field pack v1: a process
            card fills this row with its category (color dot + value, e.g. the
            hiring board's role) — same dot grammar as the unit marker. */}
        {item.kind === 'process_item' && item.category ? (
          categoryValues.length > 1 ? (
            // Several values (a candidate for two roles): one chip per value,
            // each with its own dot so the colour grammar holds per value.
            <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="card-category">
              {categoryValues.map((value) => (
                <span
                  key={value}
                  data-testid="card-category-chip"
                  className="inline-flex items-center gap-1 rounded-pill bg-sage-50 px-1.5 py-0.5 text-[0.6875rem] text-ink-soft"
                >
                  <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-pill flex-shrink-0" style={{ backgroundColor: categoryDotColor(value) ?? 'var(--color-sage-300)' }} />
                  {value}
                </span>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 mt-1.5 text-[11.5px] text-ink-soft" data-testid="card-category">
              <span aria-hidden="true" className="inline-block w-2 h-2 rounded-pill flex-shrink-0" style={{ backgroundColor: categoryDotColor(item.category) ?? 'var(--color-sage-300)' }} />
              <span className="truncate">{item.category}</span>
            </div>
          )
        ) : (item.unit_name || item.service_requested) && (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11.5px] text-ink-soft" data-testid="card-unit-service">
            {item.unit_name && (
              <>
                <span aria-hidden="true" className="inline-block w-2 h-2 rounded-pill flex-shrink-0" style={{ backgroundColor: dot ?? 'var(--color-sage-300)' }} />
                <span className="truncate">{item.unit_name}</span>
              </>
            )}
            {item.unit_name && item.service_requested && <span className="text-sage-300">·</span>}
            {item.service_requested && <span className="truncate">{displayValue(item.service_requested)}</span>}
          </div>
        )}

        {nextAction && (
          <div
            data-testid="card-next-action"
            className={'mt-1.5 text-[0.71875rem] ' + (nextActionLate ? 'text-pepper font-semibold' : 'text-ink-soft')}
          >
            Next action · {shortDate(nextAction)}
          </div>
        )}

        {/* Case Assignment — the always-visible Care manager row: name +
            initials when assigned; "Unassigned" + a ? avatar (same size/shape)
            when not. Never absent in this cluster (the ghost-state fix). */}
        {showCmRow && (
          <div className="flex items-center gap-1.5 mt-1.5 min-w-0" data-testid="card-cm-row">
            <span aria-hidden="true" className={avatarClasses(cmAssigned, 'sm')}>
              {cmAssigned ? initials(cmName) : '?'}
            </span>
            <span className="text-[11.5px] text-ink-soft truncate">
              Care manager: {cmAssigned ? cmName : 'Unassigned'}
            </span>
          </div>
        )}

        {/* Case Assignment — one-click prospect-folder access for unit leads.
            Navigation, so a link (new tab); nothing renders when no URL was
            captured. stopPropagation keeps the card's open-drawer click off. */}
        {folderUrl && (
          <div className="mt-1.5">
            <a
              href={folderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="card-folder-link"
              onClick={(e) => e.stopPropagation()}
              className="text-[11.5px] text-sage-800 underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
            >
              {folderLabel}
            </a>
          </div>
        )}

        {/* Row 3 — stage/progress chip + attachment count + owner-led avatar stack */}
        <div className="flex items-center gap-2.5 mt-3">
          {chipBits.length > 0 && (
            <span data-testid="card-stage-progress" className={'text-[11px] font-semibold rounded-pill px-2 py-0.5 ' + chipClass}>
              {chipBits.join(' · ')}
            </span>
          )}
          {/* Card attachments (0162 design §6.2): a SIBLING pill, never a
              chipBits entry. chipBits share one chipClass whose colour
              encodes card HEALTH (overdue -> pepper, complete -> sage,
              else gold); folding a file count into that string would make
              it inherit an overdue red that says nothing about files. So:
              its own neutral pill, rendered only when there is something
              to say. */}
          {attachmentCount > 0 && (
            <span
              data-testid="card-attachment-count"
              title={attachmentCount === 1 ? '1 file attached' : `${attachmentCount} files attached`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-pill px-2 py-0.5 bg-sage-50 text-sage-400"
            >
              <Paperclip size={11} aria-hidden="true" className="flex-shrink-0" />
              {attachmentCount}
            </span>
          )}
          <div className="ml-auto flex items-center" data-testid="card-assignee-stack">
            {/* Owner avatar — a button when assignable (fix #2), else a plain chip.
                Handoff discoverability fix: a hover ring (the same sage-400 token
                the focus ring already uses) so the bubble looks clickable before
                the tooltip ever shows, and a fixed, plain-language accessible
                name/tooltip — "Hand off to someone else" — instead of a state-
                dependent one, so it reads the same whether or not the case is
                currently owned. */}
            {onAssign ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAssign(item); }}
                className={
                  'relative z-30 transition-shadow duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
                  'hover:ring-2 hover:ring-sage-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                  avatarClasses(assigned)
                }
                title="Hand off to someone else"
                aria-label="Hand off to someone else"
                data-testid="card-owner-avatar"
              >
                {assigned ? initials(item.assignee_name) : '?'}
              </button>
            ) : (
              <span className={'relative z-30 ' + avatarClasses(assigned)} title={item.assignee_name ?? 'Unassigned'} data-testid="card-owner-avatar">
                {assigned ? initials(item.assignee_name) : '?'}
              </span>
            )}
            {shownCollaborators.map((c, i) => (
              <span
                key={c.user_id}
                className={'relative -ml-1.5 ring-2 ring-paper ' + avatarClasses(true, 'sm')}
                style={{ zIndex: 20 - i }}
                title={c.name}
                data-testid="card-collaborator-avatar"
              >
                {initials(c.name)}
              </span>
            ))}
            {collaboratorOverflow > 0 && (
              <span
                className="relative -ml-1.5 ring-2 ring-paper inline-flex items-center justify-center w-5 h-5 rounded-pill bg-sage-50 text-sage-400 text-[9px] font-semibold flex-shrink-0"
                title={`+${collaboratorOverflow} more`}
                data-testid="card-collaborator-overflow"
              >
                +{collaboratorOverflow}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
